"use client";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { ReactNode } from "react";
import { useRouter } from 'next/navigation'
import {
    changeAntPopieriaus,
    changeDarboVietosKaina, changeDraudejoGrupe,
    changeDraudejoTipas, changeIRankas, changeMetai, changePensijai, changeSutartiesTipas, changeTaikomasNpd, formatAsEmptyIfZero
} from "../skaiciuokle/SkaiciuoklesFunkcijos";

// inline style for numeric inputs to avoid passing unknown DOM props
// (we'll pass this directly into inputProps for each TextField)

type Props = {
    children?: ReactNode;
    duomenys: any;
    onDuomenys: (duomenys: any) => void;
}

const SkaiciuokleComponent = ({ children, duomenys, onDuomenys }: Props) => {
    function handleAntPopieriausChange(event: any) {
        onDuomenys({
            ...duomenys,
            ...changeAntPopieriaus(event.target.value, duomenys),
        });

        // ReactGA.event({
        //     category: "Calc",
        //     action: "gross",
        //     value: +event.target.value,
        // });
    }

    const handleIRankasChange = (event: any) => {
        onDuomenys({
            ...duomenys,
            ...changeIRankas(event.target.value, duomenys),
        });
        // ReactGA.event({
        //     category: "Calc",
        //     action: "net",
        //     value: +event.target.value,
        // });
    };

    const handleTaikomasNpdChange = (event: any) => {
        onDuomenys({
            ...duomenys,
            ...changeTaikomasNpd(event.target.value, duomenys),
        });
    };

    const handlePensijaiChange = (event: any) => {
        onDuomenys({
            ...duomenys,
            ...changePensijai(event.target.value, duomenys),
        });
    };

    const handleDarboVietosKainaChange = (event: any) => {
        onDuomenys({
            ...duomenys,
            ...changeDarboVietosKaina(event.target.value, duomenys),
        });
        // ReactGA.event({
        //     category: "Calc",
        //     action: "costs",
        //     value: +event.target.value,
        // });
    };

    const router = useRouter()

    const handleMetaiChange = (event: any) => {
        const newYear = event.target.value;
        try {
            // update local data; guard against any unexpected errors in calculation
            onDuomenys({ ...duomenys, ...changeMetai(newYear, duomenys) });
        } catch (err) {
            // Log and continue with navigation so UI doesn't hard-fail
            // eslint-disable-next-line no-console
            console.error('Year change handler failed, continuing navigation', err);
        }
        // navigate to the new year page regardless
        try {
            router.push(`/${newYear}`);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('router.push failed for year navigation', err);
        }
    };

    const handleDraudejoGrupeChange = (event: any) => {
        onDuomenys({
            ...duomenys,
            ...changeDraudejoGrupe(event.target.value, duomenys),
        });
    };

    const handleDraudejoTipasChange = (event: any) => {
        onDuomenys({
            ...duomenys,
            ...changeDraudejoTipas(event.target.value, duomenys),
        });
    };

    const handleSutartiesTipasChange = (event: any) => {
        onDuomenys({
            ...duomenys,
            ...changeSutartiesTipas(event.target.value, duomenys),
        });
    };

    return (
        <>
            <Grid container spacing={4}>
                <Grid size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
                    <TextField
                        fullWidth
                        InputProps={{
                            sx: { fontSize: { xs: '20px', md: '34px' } },
                            inputProps: { style: { textAlign: 'right' as const } },
                        }}
                        label={"Ant popieriaus:"}
                        autoFocus
                        onChange={handleAntPopieriausChange}
                        value={formatAsEmptyIfZero(duomenys.antPopieriaus)}
                        variant="outlined"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
                    <TextField
                        fullWidth
                        InputProps={{
                            sx: { fontSize: { xs: '20px', md: '34px' } },
                            inputProps: { style: { textAlign: 'right' as const } },
                        }}
                        label={"Į rankas:"}
                        onChange={handleIRankasChange}
                        value={formatAsEmptyIfZero(duomenys.iRankas)}
                        variant="outlined"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
                    <TextField
                        fullWidth
                        InputProps={{
                            sx: { fontSize: { xs: '20px', md: '34px' } },
                            inputProps: { style: { textAlign: 'right' as const } },
                        }}
                        label={"Darbo vietos kaina:"}
                        onChange={handleDarboVietosKainaChange}
                        value={formatAsEmptyIfZero(duomenys.darboVietosKaina)}
                        variant="outlined"
                    />
                </Grid>
            </Grid>
            <Box sx={{ pt: 5 }}>
                <Grid container spacing={4}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            select
                            label={"Neapmokestinamas pajamų dydis:"}
                            onChange={handleTaikomasNpdChange}
                            value={duomenys.taikomasNpd}
                            variant="outlined"
                        >
                            <MenuItem value="standartinis">Taikomas standartinis</MenuItem>
                            <MenuItem value="netaikomas">Netaikomas</MenuItem>
                            <MenuItem value="npd0_25">0-25% darbingumas</MenuItem>
                            <MenuItem value="npd30_55">30-55% darbingumas</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            select
                            label={"Papildomas pensijos kaupimas:"}
                            value={duomenys.pensijai}
                            onChange={handlePensijaiChange}
                            variant="outlined"
                        >
                            <MenuItem value="nekaupia">Nekaupia</MenuItem>
                            <MenuItem value="kaupiaPalaipsniui">Kaupia palaipsniui</MenuItem>
                            <MenuItem value="kaupia">Kaupia 3%</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            select
                            label={"Sutarties tipas"}
                            value={duomenys.sutartiesTipas}
                            onChange={handleSutartiesTipasChange}
                            variant="outlined"
                        >
                            <MenuItem value="neterminuota">{"Neterminuota"}</MenuItem>
                            <MenuItem value="terminuota">{"Terminuota"}</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            select
                            label={"Darbdavio tipas:"}
                            value={duomenys.draudejoTipas}
                            onChange={handleDraudejoTipasChange}
                            variant="outlined"
                        >
                            <MenuItem value="privatus">Privatus</MenuItem>
                            <MenuItem value="biudzetine">Biudžetinė įstaiga</MenuItem>
                            <MenuItem value="lietuvosBankas">Lietuvos bankas</MenuItem>
                            <MenuItem value="politinesPartijos">Politinė partija</MenuItem>
                            <MenuItem value="profesinesSajungos">Profesinė sąjunga</MenuItem>
                            <MenuItem value="religinesBendrijos">Religinė bendruomenė</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            select
                            label={"Darbdavio grupė:"}
                            value={duomenys.draudejoGrupe}
                            onChange={handleDraudejoGrupeChange}
                            variant="outlined"
                        >
                            <MenuItem value="I">I grupė</MenuItem>
                            <MenuItem value="II">II grupė</MenuItem>
                            <MenuItem value="III">III grupė</MenuItem>
                            <MenuItem value="IV">IV grupė</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            select
                            label={"Metai:"}
                            value={duomenys.metai}
                            onChange={handleMetaiChange}
                            variant="outlined"
                        >
                            {Array.from({ length: 2026 - 2019 + 1 }, (_, i) => 2019 + i).map((y) => (
                                <MenuItem key={y} value={y.toString()}>{y}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </Box >
            {children}
        </>
    )
}

export default SkaiciuokleComponent
